// R1-VISUAL Batch 1 (Slices 2, 3, 5) — Unified public landing page rewrite.
//   Slice 2: Hero + Credibility Band (navy hero photograph, brass credibility strip).
//   Slice 3: Capabilities grid (3 cards) + Process (4-step operating rhythm).
//   Slice 4: Founder story embedded via <AboutOwnerBody /> at Section 6.
//   Slice 5: Portfolio empty-state (brand photograph) + East-Coast Territory + Footer.
// All content is either static brand copy or pulled live from the public
// content service — no fabricated stats, no fake testimonials, no mock projects.
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicShell, { CapabilityTile, usePublicContent } from "../../components/public/PublicShell";
import SEOHead, { buildLocalBusinessSchema } from "../../components/public/SEOHead";
import { AboutOwnerBody } from "./AboutOwner";
import { api } from "../../lib/api";
import { ArrowRight, Compass, ClipboardList, HardHat, ShieldCheck } from "lucide-react";

const HERO_IMG = "/brand/hero_construction_navy.jpg";
const PORTFOLIO_EMPTY_IMG = "/brand/portfolio_empty_state.jpg";

const CAPABILITY_CARDS = [
  {
    slug: "new-construction",
    image: "/brand/cap_new_construction.jpg",
    title: "New Home Construction",
    body: "Ground-up custom homes and additions built with disciplined planning, structural precision and owner-led oversight from foundation to finish.",
  },
  {
    slug: "commercial-buildout",
    image: "/brand/cap_commercial_buildout.jpg",
    title: "Commercial Build-Out",
    body: "Tenant fit-outs, storefronts, offices and mixed-use rehabilitation — coordinated multi-trade delivery on schedule for owners and operators.",
  },
  {
    slug: "roofing-waterproofing",
    image: "/brand/cap_roofing_waterproofing.jpg",
    title: "Roofing & Waterproofing",
    body: "Roof replacement, envelope waterproofing and storm-damage restoration executed with material discipline and inspection-ready documentation.",
  },
];

const PROCESS_STEPS = [
  { icon: Compass,       title: "01 · Discovery",   copy: "A direct conversation with Edwin. We listen to the scope, the constraints and the outcome you're protecting." },
  { icon: ClipboardList, title: "02 · Planning",    copy: "Detailed estimate, transparent scope of work, sequenced schedule and a plain-English contract you can put your name on." },
  { icon: HardHat,       title: "03 · Build",       copy: "Owner-led jobsite oversight, disciplined trade coordination, weekly progress updates and photographic documentation." },
  { icon: ShieldCheck,   title: "04 · Close-Out",   copy: "Final punch, inspection sign-offs, warranty paperwork and the record trail every insurer, lender and municipality expects." },
];

export default function Home() {
  const c = usePublicContent();
  const [services, setServices] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [serviceAreas, setServiceAreas] = useState([]);
  const [proof, setProof] = useState({ projects_delivered: 0, states_served: 0, years_in_business: null });

  useEffect(() => {
    api.get("/public/website/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/public/website/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
    api.get("/public/website/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/public/website/service-areas").then((r) => setServiceAreas(r.data)).catch(() => {});
    api.get("/public/website/social-proof").then((r) => setProof(r.data)).catch(() => {});
  }, []);

  const heroHeadline = c.hero_headline || "Full-Service General Contracting from the Mid-Atlantic to the Southeast.";
  const heroSub = c.hero_subheadline || "New home construction, additions, remodels, commercial fit-out, historic restoration and storm-damage response — delivered with disciplined project management and owner-led craftsmanship.";

  const hero = (
    <section
      className="erf-hero-v2"
      data-testid="hero-section"
      style={{
        position: "relative",
        overflow: "hidden",
        color: "#F5F1E8",
        backgroundImage: `url(${HERO_IMG})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Navy overlay for legibility */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(90deg, rgba(11,37,69,0.92) 0%, rgba(11,37,69,0.78) 45%, rgba(11,37,69,0.55) 100%)",
        }}
      />
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "radial-gradient(700px 400px at 12% 30%, rgba(176,141,87,0.18), transparent 60%)",
        }}
      />
      <div className="erf-public-container" style={{ position: "relative", zIndex: 2, padding: "112px 24px 128px", maxWidth: 1200 }}>
        <div
          style={{
            fontFamily: '"IBM Plex Mono", monospace',
            fontSize: 12,
            letterSpacing: "0.32em",
            textTransform: "uppercase",
            color: "#D9B87A",
            marginBottom: 24,
          }}
          data-testid="hero-eyebrow"
        >
          ERF ENTERPRISES LLC · Owner-Led
        </div>
        <h1
          data-testid="hero-headline"
          style={{
            fontFamily: '"Playfair Display", Georgia, serif',
            fontSize: "clamp(1.75rem, 4.2vw + 0.5rem, 3.75rem)",
            lineHeight: 1.08,
            fontWeight: 700,
            maxWidth: 900,
            color: "#F5F1E8",
            margin: 0,
            letterSpacing: "-0.01em",
            hyphens: "none",
            WebkitHyphens: "none",
            msHyphens: "none",
            wordBreak: "normal",
            overflowWrap: "normal",
          }}
        >
          {heroHeadline}
        </h1>
        <p
          style={{
            marginTop: 24,
            maxWidth: 720,
            fontSize: 17,
            lineHeight: 1.65,
            color: "rgba(245,241,232,0.88)",
          }}
        >
          {heroSub}
        </p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 36 }}>
          <Link
            to="/quote"
            data-testid="hero-cta-quote"
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              background: "#B08D57", color: "#0B2545",
              padding: "14px 30px", fontFamily: '"Inter", sans-serif',
              fontSize: 13, fontWeight: 600, letterSpacing: "0.14em",
              textTransform: "uppercase", borderRadius: 3,
              boxShadow: "0 8px 24px -10px rgba(176,141,87,0.6)",
            }}
          >
            Request a Quote <ArrowRight size={14} />
          </Link>
          <Link
            to="/services"
            data-testid="hero-cta-services"
            style={{
              display: "inline-flex", alignItems: "center", gap: 10,
              border: "1px solid rgba(245,241,232,0.55)",
              color: "#F5F1E8", padding: "13px 28px",
              fontSize: 13, fontWeight: 500, letterSpacing: "0.14em",
              textTransform: "uppercase", borderRadius: 3, background: "transparent",
            }}
          >
            See What We Do
          </Link>
        </div>
      </div>

      {/* Credibility Band — subtle brass rule + evenly-spaced facts. */}
      <div
        data-testid="credibility-band"
        style={{
          position: "relative", zIndex: 2,
          borderTop: "1px solid rgba(176,141,87,0.55)",
          background: "rgba(6,22,44,0.62)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        }}
      >
        <div
          className="erf-public-container erf-credibility-grid"
          style={{
            maxWidth: 1200,
            padding: "22px 24px",
            display: "grid",
            gridTemplateColumns: "1fr",
            gap: 24,
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {/* Marketing-Readiness Pass — removed unsupported claims (35+ years,
              HICPA/license number, primary-states count). Retained only the
              owner-led statement + territory summary sourced verbatim from
              the Owner's authoritative content decision. */}
          <div data-testid="credibility-territory" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 20, fontWeight: 600, color: "#D9B87A", letterSpacing: "0.01em" }}>
              Pennsylvania through Florida — with reach anywhere on the East Coast
            </div>
            <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(245,241,232,0.7)" }}>
              Owner-led · Edwin is on every project
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 780px) {
          .erf-credibility-grid { grid-template-columns: repeat(2, 1fr) !important; row-gap: 18px !important; }
        }
      `}</style>
    </section>
  );

  return (
    <PublicShell hero={hero}>
      <SEOHead
        title="ERF ENTERPRISES LLC — General Contractor · Pennsylvania through Florida, East Coast corridor"
        description="ERF ENTERPRISES LLC — full-service general contracting from Pennsylvania through Florida, with reach anywhere on the East Coast."
        jsonLd={buildLocalBusinessSchema({ content: c, states: serviceAreas })}
      />

      {/* SLICE 3.a — Capabilities grid (three curated brand cards). */}
      <section className="erf-public-section" data-testid="capabilities-section" style={{ background: "#FAF7F2" }}>
        <div className="erf-public-container" style={{ maxWidth: 1200 }}>
          <div className="erf-public-section-head">
            <div className="erf-public-section-head-copy">
              <div className="erf-public-eyebrow">Capabilities</div>
              <h2>Three disciplines. One standard of care.</h2>
              <p>Every capability is delivered by our own coordinated team under Edwin's direct oversight. No white-labelled subcontract, no anonymous project manager.</p>
            </div>
            <Link to="/services" className="erf-public-cta-outline erf-public-section-head-cta" data-testid="capabilities-view-all">View all services</Link>
          </div>
          <div
            className="erf-capabilities-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: 24,
              marginTop: 36,
            }}
          >
            {CAPABILITY_CARDS.map((cap) => (
              <article
                key={cap.slug}
                className="erf-capability-card"
                data-testid={`capability-card-${cap.slug}`}
                style={{
                  background: "#FFFFFF",
                  border: "1px solid rgba(11, 37, 69, 0.15)",
                  borderTop: "3px solid #3B82C4",
                  borderRadius: 4,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  boxShadow: "0 12px 30px -20px rgba(11,37,69,0.35)",
                  transition: "transform .35s ease, box-shadow .35s ease",
                }}
              >
                <div style={{ aspectRatio: "16/10", overflow: "hidden", background: "#F4F4EE" }}>
                  <img
                    src={cap.image}
                    alt={`${cap.title} — ERF ENTERPRISES LLC`}
                    loading="lazy"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                  />
                </div>
                <div style={{ padding: "24px 24px 26px", display: "flex", flexDirection: "column", gap: 12, flex: 1 }}>
                  <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#B08D57" }}>Capability</div>
                  <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 22, color: "#0B2545", margin: 0, lineHeight: 1.2 }}>{cap.title}</h3>
                  <p style={{ fontSize: 14.5, lineHeight: 1.65, color: "#1F2937", margin: 0, opacity: 0.88 }}>{cap.body}</p>
                  <Link
                    to={`/services/${cap.slug}`}
                    style={{
                      marginTop: "auto", paddingTop: 12,
                      color: "#0B2545", fontSize: 12, fontWeight: 600,
                      letterSpacing: "0.16em", textTransform: "uppercase",
                      display: "inline-flex", alignItems: "center", gap: 8,
                    }}
                  >
                    Learn more <ArrowRight size={13} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <style>{`
            @media (max-width: 960px) {
              .erf-capabilities-grid { grid-template-columns: 1fr !important; }
            }
            @media (prefers-reduced-motion: no-preference) {
              .erf-capability-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 20px 40px -20px rgba(11, 37, 69, 0.45);
              }
            }
          `}</style>
        </div>
      </section>

      {/* SLICE 3.b — Process (four-step operating rhythm). */}
      <section className="erf-public-section-alt" data-testid="process-section" style={{ background: "#0B2545", color: "#F5F1E8" }}>
        <div className="erf-public-container" style={{ maxWidth: 1200 }}>
          <div style={{ maxWidth: 640 }}>
            <div className="erf-public-eyebrow" style={{ color: "#D9B87A" }}>How we work</div>
            <h2 style={{ color: "#F5F1E8" }}>A four-step operating rhythm — plain, predictable, documented.</h2>
            <p style={{ color: "rgba(245,241,232,0.8)", marginTop: 12 }}>
              Construction succeeds when the plan is honest and the record is complete. Every ERF project moves through the same disciplined cadence.
            </p>
          </div>

          <div
            className="erf-process-grid"
            style={{
              marginTop: 44,
              display: "grid",
              gridTemplateColumns: "repeat(4, 1fr)",
              gap: 22,
            }}
          >
            {PROCESS_STEPS.map(({ icon: Icon, title, copy }) => (
              <div
                key={title}
                data-testid={`process-step-${title.split(" ")[0]}`}
                style={{
                  border: "1px solid rgba(217,184,122,0.35)",
                  borderRadius: 4,
                  padding: "26px 22px 24px",
                  background: "rgba(255,255,255,0.03)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                <Icon size={26} color="#D9B87A" strokeWidth={1.4} />
                <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 18, color: "#F5F1E8", fontWeight: 600, letterSpacing: "0.01em" }}>
                  {title}
                </div>
                <div style={{ fontSize: 13.5, lineHeight: 1.65, color: "rgba(245,241,232,0.78)" }}>{copy}</div>
              </div>
            ))}
          </div>

          <style>{`
            @media (max-width: 1024px) {
              .erf-process-grid { grid-template-columns: repeat(2, 1fr) !important; }
            }
            @media (max-width: 560px) {
              .erf-process-grid { grid-template-columns: 1fr !important; }
            }
          `}</style>
        </div>
      </section>

      {/* SLICE 4 — About the Owner embedded body. */}
      <div data-testid="landing-about-owner-embed" style={{ background: "#FAF7F2" }}>
        <AboutOwnerBody compact />
      </div>

      {/* SLICE 5.a — Portfolio (real data if present; brand empty state otherwise). */}
      <section className="erf-public-section" data-testid="portfolio-section" style={{ background: "#FFFFFF" }}>
        <div className="erf-public-container" style={{ maxWidth: 1200 }}>
          <div className="erf-public-section-head">
            <div className="erf-public-section-head-copy">
              <div className="erf-public-eyebrow">Recent work</div>
              <h2>Portfolio.</h2>
              <p>Every project delivered by ERF is added to this record. When completed work is on file, you'll see it here first.</p>
            </div>
            {portfolio.length > 0 && (
              <Link to="/portfolio" className="erf-public-cta-outline erf-public-section-head-cta" data-testid="portfolio-view-all">View all projects</Link>
            )}
          </div>

          {portfolio.length === 0 ? (
            <div
              data-testid="portfolio-empty-preview"
              style={{
                marginTop: 36,
                display: "grid",
                gridTemplateColumns: "1.05fr 0.95fr",
                gap: 40,
                alignItems: "stretch",
                border: "1px solid #E5E1DA",
                borderRadius: 4,
                overflow: "hidden",
                background: "#FAF7F2",
              }}
              className="erf-portfolio-empty"
            >
              <div style={{ minHeight: 320, background: "#0B2545" }}>
                <img
                  src={PORTFOLIO_EMPTY_IMG}
                  alt="ERF ENTERPRISES LLC completed jobsite documentation."
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>
              <div style={{ padding: "40px 40px 40px 8px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 16 }}>
                <div style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "#B08D57" }}>
                  Portfolio in progress
                </div>
                <h3 style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 26, color: "#0B2545", margin: 0, lineHeight: 1.2 }}>
                  Our portfolio grows with each completed project.
                </h3>
                <p style={{ fontSize: 15, lineHeight: 1.7, color: "#1F2937", opacity: 0.88, margin: 0 }}>
                  Completed jobsite documentation, references and reference-owner introductions are shared directly with prospective clients during consultation.
                </p>
                <Link
                  to="/quote"
                  style={{
                    marginTop: 6, alignSelf: "flex-start",
                    background: "#0B2545", color: "#F5F1E8",
                    padding: "12px 26px", fontSize: 12, fontWeight: 600,
                    letterSpacing: "0.14em", textTransform: "uppercase", borderRadius: 3,
                  }}
                  data-testid="portfolio-empty-cta"
                >
                  Start a conversation
                </Link>
              </div>

              <style>{`
                @media (max-width: 900px) {
                  .erf-portfolio-empty { grid-template-columns: 1fr !important; }
                  .erf-portfolio-empty > div:nth-child(2) { padding: 30px 26px !important; }
                }
              `}</style>
            </div>
          ) : (
            <div className="erf-public-services-grid" style={{ marginTop: 36 }}>
              {portfolio.slice(0, 3).map((p) => (
                <Link key={p.id} to={`/portfolio/${p.id}`} className="erf-public-service-card">
                  <CapabilityTile service={{ slug: p.service_slug, name: p.project_name }} />
                  <div className="erf-public-service-card-body">
                    <div className="erf-public-service-card-eyebrow">{p.city}, {p.state}</div>
                    <h3>{p.project_name}</h3>
                    <p>{p.summary}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SLICE 5.b — Territory: exact phrasing per Owner authoritative decision. */}
      <section className="erf-public-section-alt" data-testid="territory-section" style={{ background: "#F5F1E8" }}>
        <div className="erf-public-container" style={{ maxWidth: 1200 }}>
          <div className="erf-public-map-band">
            <div>
              <div className="erf-public-eyebrow">Where we work</div>
              <h2>Pennsylvania through Florida — with reach anywhere on the East Coast.</h2>
              <p style={{ color: "#1F2937", lineHeight: 1.7, marginTop: 20, opacity: 0.88 }} data-testid="territory-body">
                {c.service_area_note}
              </p>
              <Link to="/service-areas" className="erf-public-cta-outline" style={{ marginTop: 24, display: "inline-block" }} data-testid="territory-cta">
                See coverage map
              </Link>
            </div>
            <EastCoastMap serviceAreas={serviceAreas} />
          </div>
        </div>
      </section>

      {/* Testimonials — only if real ones exist (directive compliance). */}
      {testimonials.length > 0 && (
        <section className="erf-public-section" data-testid="testimonials-section" style={{ background: "#FFFFFF" }}>
          <div className="erf-public-container" style={{ maxWidth: 1200 }}>
            <div className="erf-public-eyebrow">Clients</div>
            <h2 style={{ marginBottom: 40 }}>What clients say.</h2>
            <div className="erf-public-services-grid">
              {testimonials.slice(0, 3).map((t) => (
                <div key={t.id} className="erf-public-service-card" style={{ padding: "28px 26px" }}>
                  <p style={{ fontStyle: "italic", color: "#0B2545", lineHeight: 1.7, marginBottom: 20, fontSize: 16 }}>
                    "{t.quote_text}"
                  </p>
                  <div style={{ fontWeight: 600, color: "#0B2545" }}>{t.author_name}</div>
                  {t.author_role && (<div style={{ fontSize: 13, color: "#1F2937", opacity: 0.7 }}>{t.author_role}</div>)}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA band — Marketing-Readiness Pass removed the projects/states/years
          social-proof ticker (no authoritative records back those figures). */}
      <section className="erf-public-cta-band" data-testid="cta-band" style={{ background: "#0B2545", color: "#F5F1E8" }}>
        <div className="erf-public-container" style={{ maxWidth: 1200 }}>
          <h2 style={{ color: "#F5F1E8" }}>{c.cta_headline || "Ready to build? Let's talk."}</h2>
          <p style={{ color: "rgba(245,241,232,0.85)" }}>{c.cta_body || "Tell us about your project and we'll get back to you within one business day."}</p>
          <Link to="/quote" className="erf-public-cta" data-testid="footer-cta-quote" style={{ background: "#B08D57", color: "#0B2545", marginTop: 24, display: "inline-block" }}>
            Request a Quote
          </Link>
        </div>
      </section>
    </PublicShell>
  );
}

function CredCell({ k, v }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div style={{ fontFamily: '"Playfair Display", Georgia, serif', fontSize: 20, fontWeight: 600, color: "#D9B87A", letterSpacing: "0.01em" }}>{k}</div>
      <div style={{ fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(245,241,232,0.7)" }}>{v}</div>
    </div>
  );
}

function ProofCell({ value, label }) {
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ fontSize: 28, fontWeight: 600, color: "#D9B87A", fontFamily: '"Playfair Display", serif' }}>{value}</div>
      <div style={{ fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(245,241,232,0.7)", marginTop: 4 }}>{label}</div>
    </div>
  );
}

function corridorHeadline(areas) {
  if (!areas || areas.length === 0) {
    return "Serving the East Coast corridor — with reach anywhere the opportunity calls.";
  }
  const primary = areas.filter((a) => a.tier === "primary").sort((a, b) => a.order - b.order);
  if (primary.length < 2) return `${primary[0]?.state_name || "The East Coast"} — with reach anywhere on the East Coast.`;
  return `${primary[0].state_name} through ${primary[primary.length - 1].state_name} — with reach anywhere on the East Coast.`;
}

function corridorFallbackNote(areas) {
  const names = corridorStateList(areas);
  return `ERF ENTERPRISES LLC serves clients across the East Coast corridor — ${names} — as our primary market. For the right opportunity, we travel — reach out and let's discuss your project.`;
}

function corridorStateList(areas) {
  if (!areas || areas.length === 0) return "Pennsylvania through Florida";
  const primary = areas.filter((a) => a.tier === "primary").sort((a, b) => a.order - b.order);
  if (primary.length === 0) return "Pennsylvania through Florida";
  if (primary.length === 1) return primary[0].state_name;
  return primary.slice(0, -1).map((a) => a.state_name).join(", ") + ", and " + primary[primary.length - 1].state_name;
}

function EastCoastMap({ serviceAreas = [] }) {
  const markers = (serviceAreas || [])
    .filter((a) => a.tier === "primary")
    .sort((a, b) => a.order - b.order)
    .map((a, i, arr) => ({
      label: a.state_code,
      y: 55 + (i * (230 / Math.max(1, arr.length - 1))),
    }));
  const fallback = markers.length > 0 ? markers : [
    { y: 55, label: "PA" }, { y: 78, label: "NJ" }, { y: 100, label: "DE" },
    { y: 122, label: "MD" }, { y: 142, label: "DC" }, { y: 164, label: "VA" },
    { y: 186, label: "NC" }, { y: 208, label: "SC" }, { y: 230, label: "GA" }, { y: 254, label: "FL" },
  ];
  return (
    <div className="erf-public-map-svg" data-testid="east-coast-map">
      <svg viewBox="0 0 400 340" width="100%" role="img" aria-label={`East Coast corridor — ${fallback.map((f) => f.label).join(", ")} — service area`}>
        <defs>
          <linearGradient id="coast-fill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#B08D57" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#5B7C6E" stopOpacity="0.9" />
          </linearGradient>
        </defs>
        <path d="M 200 40 Q 220 60, 230 90 Q 240 120, 245 150 Q 250 180, 255 210 Q 258 240, 250 265 L 220 275 L 210 250 Q 208 220, 205 195 Q 202 165, 198 135 Q 194 105, 192 78 Q 195 55, 200 40 Z"
          fill="url(#coast-fill)" opacity="0.85" />
        {fallback.map((m) => (
          <g key={m.label}>
            <circle cx="220" cy={m.y} r="4" fill="#FAF7F2" stroke="#1F2937" strokeWidth="1.5" />
            <text x="235" y={m.y + 4} fill="#1F2937" fontFamily="Inter, sans-serif" fontSize="11" fontWeight="600">{m.label}</text>
          </g>
        ))}
        <text x="20" y="30" fill="#B08D57" fontFamily="Inter, sans-serif" fontSize="11" letterSpacing="0.2em" fontWeight="600">PRIMARY SERVICE CORRIDOR</text>
        <text x="20" y="320" fill="#64748B" fontFamily="Inter, sans-serif" fontSize="10" fontStyle="italic">Available for the right opportunity anywhere on the East Coast.</text>
      </svg>
    </div>
  );
}
